/*
    Copyright 2022 Dynatrace LLC

    Licensed under the Apache License, Version 2.0 (the "License");
    you may not use this file except in compliance with the License.
    You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

    Unless required by applicable law or agreed to in writing, software
    distributed under the License is distributed on an "AS IS" BASIS,
    WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
    See the License for the specific language governing permissions and
    limitations under the License.
*/

import * as http from "http";
import * as https from "https";
import { Socket } from "net";
import * as path from "path";
import { performance } from "perf_hooks";
import * as url from "url";
import Long = require("long");

import { context, ROOT_CONTEXT } from "@opentelemetry/api";
import { ExportResult, ExportResultCode, suppressTracing } from "@opentelemetry/core";
import { SpanExporter, ReadableSpan } from "@opentelemetry/sdk-trace-base";

import { ExportType } from "../Types";
import { ComponentLogger } from "../Logger";
import { getConfiguration } from "../configuration/StartupConfig";
import { detectResource } from "../trace/Resource";
import { prettyPrintObject, verboseFormatException } from "../utils/LoggingUtils";
import { setTimeoutUnref } from "../utils/Timers";
import { checkNumericOption } from "../utils/Validators";
import { VERSIONS } from "../version";
import { serialize, serializeResource } from "./Serialization";

// ============================================================================

const cSpansPath = "odin/v1/spans";
const cMaxRetries = 2;
const cDefaultFastConnectionTimeoutMs = 1_000;
const cDefaultFastDataTimeoutMs = 5_000;
const cDefaultNormalConnectionTimeoutMs = 10_000;
const cDefaultNormalDataTimeoutMs = 60_000;
const cConnectionResetTimeLimitMs = 300;

// ----------------------------------------------------------------------------
export interface DtSpanExporterConfig {
	baseUrl: string;
	authToken: string;
	tenantUUID: string;
	agentID: Long;
	fastConnectionTimeoutMs?: number;
	fastDataTimeoutMs?: number;
	normalConnectionTimeoutMs?: number;
	normalDataTimeoutMs?: number;
}

// ----------------------------------------------------------------------------
interface Timeouts {
	connection: number;
	data: number;
}

// ----------------------------------------------------------------------------
export type ResultCallback = (result: ExportResult) => void;

// ----------------------------------------------------------------------------
export class DtSpanExporter implements SpanExporter {
	constructor(cfg?: Readonly<DtSpanExporterConfig>) {
		const config = { ...getConfiguration(), ...cfg };
		if (config == null || typeof(config) !== "object") {
			throw new TypeError("DtSpanExporter: config invalid");
		}
		if (typeof(config.baseUrl) !== "string") {
			throw new TypeError("DtSpanExporter: baseUrl must be a string");
		}
		if (typeof(config.authToken) !== "string") {
			throw new TypeError("DtSpanExporter: authToken must be a string");
		}
		if (typeof(config.tenantUUID) !== "string") {
			throw new TypeError("DtSpanExporter: tenantUUID must be a string");
		}
		if (typeof(config.agentID) !== "object") {
			throw new TypeError("DtSpanExporter: agentID must be a Long");
		}

		this._fastTimeoutsMs = {
			connection: checkNumericOption("DtSpanExporter", config, "fastConnectionTimeoutMs", cDefaultFastConnectionTimeoutMs),
			data: checkNumericOption("DtSpanExporter", config, "fastDataTimeoutMs", cDefaultFastDataTimeoutMs)
		};

		this._normalTimeoutsMs = {
			connection: checkNumericOption("DtSpanExporter", config, "normalConnectionTimeoutMs", cDefaultNormalConnectionTimeoutMs),
			data: checkNumericOption("DtSpanExporter", config, "normalDataTimeoutMs", cDefaultNormalDataTimeoutMs)
		};

		const urlObj = new url.URL(config.baseUrl);
		let proto: typeof https | typeof http;
		switch (urlObj.protocol) {
			case "http:": proto = http; break;
			case "https:": proto = https; break;
			default: throw new RangeError("DtSpanExporter: options.baseUrl protocol unsupported");
		}
		this._httpRequest = proto.request;
		this._tenantUUID = config.tenantUUID;
		this._senderId = config.agentID;

		this._reqOpts = {
			method: "POST",
			hostname: urlObj.hostname,
			port: urlObj.port,
			path: path.posix.join(urlObj.pathname, cSpansPath),
			agent: new proto.Agent({
				keepAlive: true,
				maxSockets: 1
			}),
			headers: {
				"Content-Type": "application/x-dt-span-export",
				"Authorization": `dynatrace ${config.authToken}`,
				"User-Agent": `odin-nodejs/${VERSIONS.fullVersion} 0x${Buffer.from(this._senderId.toBytes()).toString("hex")} ${config.tenantUUID}`
			},
			rejectUnauthorized: this._isSecurityEnhancedDomain(urlObj.hostname)
		};
	}

	public hasPendingRequest(): boolean {
		return this._hasPendingRequest;
	}

	/**
	 * Exports spans.
	 */
	public export(spans: ReadableSpan[], resultCallback: ResultCallback, type = ExportType.Normal, retries = 0, spanExportMsg?: Buffer): void {
		if (spans.length === 0) {
			process.nextTick(resultCallback, { code: ExportResultCode.SUCCESS });
			return;
		}

		if (this._isShutdown || this._request) {
			process.nextTick(resultCallback, { code: ExportResultCode.FAILED });
			return;
		}

		context.with(this._suppressingContext, () => {
			if (this._resource == null) {
				// Save the resource on the first export
				const resource = detectResource().merge(spans[0].resource);
				this._resource = serializeResource(resource);
				this._logger.info(`Resource elements: ${Object.keys(resource.attributes).length}, size: ${this._resource.length}`);
				if (this._logger.debugEnabled) {
					this._logger.debug(`Resource values: ${prettyPrintObject(resource.attributes)}`);
				}
			}

			// encode all Spans into SpanExport messages
			spanExportMsg = spanExportMsg ?? serialize(this._senderId, this._tenantUUID, spans, this._resource);

			if (this._logger.debugEnabled) {
				this._logger.debug(`export: spanCnt: ${spans.length}, size: ${spanExportMsg.length}, type: ${ExportType[type]}, retries: ${retries}`);
			}
			this._executeRequest(spanExportMsg, spans, resultCallback, type, retries);
		});
	}

	/**
	 * Shutdown exporter
	 */
	public shutdown(): Promise<void> {
		this._logger.info("shutdown");
		this._isShutdown = true;
		return Promise.resolve();
	}

	/**
	 * Abort an ongoing export by destroying the underlying socket.
	 */
	public abort(): void {
		if (this._request) {
			this._logger.debug("abort");
			this._request.destroy();
		}
	}

	private _executeRequest(spanExportMsg: Buffer, spans: ReadableSpan[], resultCallback: ResultCallback, type: ExportType, retries: number): void {
		// set timeout depending of connection state of socket
		const timeouts = (type === ExportType.ForceFlush) ? this._fastTimeoutsMs : this._normalTimeoutsMs;
		const self = this;

		function onResponse(res: http.IncomingMessage): void {
			if (self._logger.debugEnabled) {
				self._logger.debug(`request#onResponse: statusCode: ${res.statusCode}`);
			}

			self._stopTimer();

			res.resume();	// discard any incoming data

			res.on("error", (e) => {
				// no need for handling a response error as a valid statusCode has
				// been received before which indicates message was received
				self._logger.debug(`response#error: ${verboseFormatException(e)}`);
			});

			if (res.statusCode != null && res.statusCode >= 200 && res.statusCode < 300) {
				process.nextTick(resultCallback, { code: ExportResultCode.SUCCESS });
			} else if (res.statusCode === 401 || res.statusCode === 403) {
				self._logger.warn("Not authorized to send spans");
				// 401/403 is permanent
				self._isShutdown = true;
				process.nextTick(resultCallback, { code: ExportResultCode.FAILED });
			} else {
				self._logger.warn(`Received status code ${res.statusCode}`);
				process.nextTick(resultCallback, { code: ExportResultCode.FAILED });
			}

			self._request = undefined;
		}

		function onRequestError(e: NodeJS.ErrnoException): void {
			const connecting = self._connecting;

			self._connecting = false;
			self._stopTimer();
			self._request = undefined;

			if (self._logger.debugEnabled) {
				self._logger.debug(`request#error: connecting: ${connecting}\n${verboseFormatException(e)}`);
			}

			// when connection is reset by the server, retry only once with no delay if send operation didn't take longer than cConnectionResetTimeLimitMs
			if (self._shouldRetrySendOperation(retries, e.code!)) {
				self._logger.debug("retrying export due to connection reset");
				self.export(spans, resultCallback, type, retries + 1, spanExportMsg);
				return;
			}

			if (!connecting) {
				// error after connection before response was received. do not retry (possibly some chunks already sent)
				process.nextTick(resultCallback, { code: ExportResultCode.FAILED });
				return;
			}

			if (type === ExportType.ForceFlush) {
				// do not retry on errors during force flush
				process.nextTick(resultCallback, { code: ExportResultCode.FAILED });
				return;
			}

			if (retries < cMaxRetries) {
				const retryMs = self._getDelayMs(retries);
				if (self._logger.debugEnabled) {
					self._logger.debug(`retry in ${retryMs}ms`);
				}
				setTimeoutUnref(() => self.export(spans, resultCallback, type, retries + 1, spanExportMsg), retryMs);
				return;
			}

			process.nextTick(resultCallback, { code: ExportResultCode.FAILED });
		}

		function onSocket(socket: Socket): void {
			if (self._logger.debugEnabled) {
				self._logger.debug(`request#socket: connecting: ${socket.connecting}`);
			}

			if (socket.connecting) {
				socket.once("connect", onSocketConnect);
			} else {
				onSocketConnect();
			}
		}

		function onSocketConnect(): void {
			self._logger.debug("socket#connect");

			self._connecting = false;
			self._stopTimer();
			// monitor data transfer - end conditions:
			// error event, response event
			self._timer = setTimeoutUnref(() => {
				self._logger.debug("timeout during data transfer");
				self.abort();
			}, timeouts.data);
			self._sendStartTime = performance.now();
		}

		try {
			this._hasPendingRequest = true;
			this._request = this._httpRequest(this._reqOpts, onResponse);
			this._request.on("error", onRequestError);
			this._request.on("socket", onSocket);
			this._request.end(spanExportMsg);

			// monitor connection setup - end conditions:
			// error event, socket event with connected socket or connect event on socket
			this._timer = setTimeoutUnref(() => {
				this._logger.debug("timeout during connection setup");
				this.abort();
			}, timeouts.connection);
			this._connecting = true;
		} catch (e) {
			// this is clearly a setup issue as we provide invalid data => shutdown exporter
			this._logger.logAgentException(e);
			this._isShutdown = true;
			process.nextTick(resultCallback, { code: ExportResultCode.FAILED });
		} finally {
			this._hasPendingRequest = false;
		}
	}

	private _getDelayMs(retries: number): number {
		const [baseDelaySeconds, skewRangeSeconds] = (retries === 0) ? [5, 1] : [15, 5];
		const skew = (Math.random() - 0.5) * skewRangeSeconds * 2;
		return Math.round((baseDelaySeconds + skew) * 1000);
	}

	private _isSecurityEnhancedDomain(domain: string): boolean {
		return (domain.indexOf(".dynatrace.com") !== -1) ||
			(domain.indexOf(".dynatracelabs.com") !== -1) ||
			(domain.indexOf(".ruxit.com") !== -1) ||
			(domain.indexOf(".ruxitlabs.com") !== -1);
	}

	private _stopTimer(): void {
		if (this._timer != null) {
			clearTimeout(this._timer);
		}
		this._timer = undefined;
	}

	private _shouldRetrySendOperation(retries: number, errorCode: string) {
		let shouldRetry = false;
		const elapsedTimeMs = performance.now() - this._sendStartTime!;
		if (retries === 0 && elapsedTimeMs < cConnectionResetTimeLimitMs && errorCode === "ECONNRESET") {
			shouldRetry = true;
		}

		return shouldRetry;
	}

	private _request?: http.ClientRequest;
	private _timer?: NodeJS.Timeout;
	private _sendStartTime?: number;
	private _connecting = false;

	private _isShutdown = false;
	private readonly _reqOpts: https.RequestOptions;
	private readonly _httpRequest: typeof http.request;
	private readonly _senderId: Long;
	private readonly _tenantUUID: string;
	private _resource?: Buffer;
	private _hasPendingRequest = false;
	private readonly _fastTimeoutsMs: Timeouts;
	private readonly _normalTimeoutsMs: Timeouts;
	private readonly _logger = new ComponentLogger("Exporter");
	private readonly _suppressingContext = suppressTracing(ROOT_CONTEXT);
}
